import { matchesRoute } from 'features/auth/utils/appRenderRule';
import { hasStoredIdentityUser } from 'features/auth/utils/identityOidcConfig';
import { isStoredSessionStale } from 'features/auth/utils/lastActivity';
import { preconnect, type PreconnectHint } from 'features/network/preconnect';
import { RoutePath } from 'features/routing/constants';

export type SignInHintEnv = {
  mode: string;
  functionsRegion: string | undefined;
  projectId: string | undefined;
};

const FIRESTORE_ORIGIN = 'https://firestore.googleapis.com';

const originOf = (href: string): string | null => {
  try {
    return new URL(href).origin;
  } catch {
    return null;
  }
};

// A preconnect serves only requests with the same credentials mode: Firestore's
// carry credentials, the functions host's do not.
export const signInHints = (env: SignInHintEnv): PreconnectHint[] => {
  if (env.mode === 'development') {
    return [];
  }

  const hints: PreconnectHint[] = [
    { origin: FIRESTORE_ORIGIN, crossOrigin: false },
  ];
  const functionsOrigin =
    env.functionsRegion && env.projectId
      ? originOf(
          `https://${env.functionsRegion}-${env.projectId}.cloudfunctions.net`
        )
      : null;

  if (functionsOrigin !== null) {
    hints.push({ origin: functionsOrigin, crossOrigin: true });
  }

  return hints;
};

export const preconnectSignInHosts = (): void => {
  preconnect(
    signInHints({
      mode: import.meta.env.MODE,
      functionsRegion: import.meta.env.PUBLIC_FIREBASE_CLOUD_FUNCTIONS_REGION,
      projectId: import.meta.env.PUBLIC_FIREBASE_PROJECT_ID,
    })
  );
};

export const preconnectSignInHostsAtLoad = (): void => {
  const { pathname } = window.location;

  try {
    if (
      matchesRoute(RoutePath.AuthCallback, pathname) ||
      (!matchesRoute(RoutePath.SignedOut, pathname) &&
        hasStoredIdentityUser() &&
        !isStoredSessionStale())
    ) {
      preconnectSignInHosts();
    }
  } catch {
    // Blocked storage adds no hints.
  }
};
